import React, { useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  TablePagination,
  Box,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DeleteIcon from '@mui/icons-material/Delete';
import { getDownloadUrl, getTranscriptionDownloadUrl } from '../services/api';
import { formatFileSize, formatDate } from '../utils';

const HistoryTable = ({ 
  history = [], 
  onPreview, 
  onClearHistory, 
  onDeleteItem, 
  clearHistoryLabel = "Limpiar historial" 
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Filtra historias duplicadas
  const uniqueHistory = history.filter(
    (item, index, self) =>
      index === self.findIndex(
        (t) =>
          t.originalFilename === item.originalFilename &&
          t.processedDate === item.processedDate &&
          t.operation === item.operation
      )
  ).sort((a, b) => new Date(b.processedDate) - new Date(a.processedDate));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearHistoryClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmClear = () => {
    onClearHistory();
    setConfirmDialogOpen(false);
  };

  const handleCancelClear = () => {
    setConfirmDialogOpen(false);
  };

  const handleDeleteItemClick = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteItem = () => {
    if (selectedItem && onDeleteItem) {
      onDeleteItem(selectedItem);
    }
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const handleCancelDeleteItem = () => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteSweepIcon />}
          onClick={handleClearHistoryClick}
        >
          {clearHistoryLabel}
        </Button>
      </Box>
      
      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table stickyHeader aria-label="historia de procesamiento">
          <TableHead>
            <TableRow>
              <TableCell>Archivo Original</TableCell>
              <TableCell>Operación</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Tamaño Original</TableCell>
              <TableCell>Tamaño Procesado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uniqueHistory
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((item, index) => (
                <TableRow key={`${item.originalFilename}-${item.processedDate}-${index}`}>
                  <TableCell component="th" scope="row">
                    {item.originalFilename}
                  </TableCell>
                  <TableCell>{item.operation}</TableCell>
                  <TableCell>{formatDate(item.processedDate)}</TableCell>
                  <TableCell>
                    {item.originalSize && item.originalSize !== "N/A"
                      ? formatFileSize(parseInt(item.originalSize))
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    {item.processedSize && item.processedSize !== "N/A"
                      ? formatFileSize(parseInt(item.processedSize))
                      : "N/A"}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {item.downloadLink && (
                        <>
                          {item.downloadLinks ? (
                            <>
                              <Tooltip title="Descargar JSON">
                                <IconButton
                                  color="primary"
                                  component="a"
                                  href={item.operation === "Transcripción" 
                                    ? getTranscriptionDownloadUrl(item.processedFilename) 
                                    : (item.downloadLinks.json || item.downloadLink)}
                                  download
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Descargar TXT (solo texto)">
                                <IconButton
                                  color="info"
                                  component="a"
                                  href={item.operation === "Transcripción" 
                                    ? getTranscriptionDownloadUrl(item.processedFilename, "txt") 
                                    : item.downloadLinks.txt}
                                  download
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Descargar">
                              <IconButton
                                color="primary"
                                component="a"
                                href={getDownloadUrl(item.processedFilename)}
                                download
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                      <Tooltip title="Vista Previa">
                        <IconButton
                          color="secondary"
                          onClick={() => onPreview(item.processedFilename)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {onDeleteItem && (
                        <Tooltip title="Eliminar">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteItemClick(item)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={uniqueHistory.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
      
      {/* Diálogo de confirmación */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelClear}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"¿Confirmar eliminación?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Esta acción eliminará todos los archivos de entrada, salida, procesados y registros de progreso.
            Los archivos no podrán ser recuperados. ¿Estás seguro?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClear}>Cancelar</Button>
          <Button onClick={handleConfirmClear} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo de confirmación para eliminar un registro específico */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDeleteItem}
        aria-labelledby="delete-item-dialog-title"
        aria-describedby="delete-item-dialog-description"
      >
        <DialogTitle id="delete-item-dialog-title">
          {"¿Eliminar este archivo?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-item-dialog-description">
            Se eliminarán todos los archivos relacionados con: 
            <strong>{selectedItem?.originalFilename}</strong>.
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteItem}>Cancelar</Button>
          <Button onClick={handleConfirmDeleteItem} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoryTable; 